/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import WindIcon from '@mui/icons-material/Air';
import BatteryIcon from '@mui/icons-material/BatteryChargingFull';
import PlantIcon from '@mui/icons-material/Power';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SolarIcon from '@mui/icons-material/SolarPower';
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ListResultsQuery, listToString, type ProjectListEntry } from '@poweralpha/common';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import PvsIcon from '../../../icons/pvs.svg?react';
import { Emoji } from '../../../react-components/Emoji';
import RecordList from '../../../react-components/RecordList';
import { InfoPill } from '../../../react-components/text';
import Widget from '../../../react-components/Widget';

const queryLimit = 10;
const clearHoveredProjectDelayMs = 150;

const formatPoiSize = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value) || value <= 0) {
    return null;
  }

  return `${value.toFixed(1)} MW`;
};

function PlantTypeIcon({ type }: { type: string | null }) {
  const normalized = type?.toUpperCase() ?? '';
  if (normalized.includes('PV') && normalized.includes('S')) return <PvsIcon height={16} />;
  if (normalized.includes('PV')) return <SolarIcon fontSize="small" />;
  if (normalized.includes('S')) return <BatteryIcon fontSize="small" />;
  if (normalized.includes('WIND')) return <WindIcon fontSize="small" />;
  return <PlantIcon fontSize="small" />;
}

const getRow = (project: ProjectListEntry) => {
  const poiSizeLabel = formatPoiSize(project.expected_poi_capacity);

  return (
    <>
      <Tooltip title={`Power Plant Type: ${project.plant_type ?? '-'}`}>
        <Box
          sx={{
            color: 'text.secondary',
            lineHeight: 0,
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <PlantTypeIcon type={project.plant_type} />
        </Box>
      </Tooltip>
      <Stack
        spacing={0.5}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'center',
          }}
        >
          <MuiLink
            component="span"
            underline="always"
            sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
          >
            {project.name}
          </MuiLink>
          {Boolean(project.roles?.length) && (
            <Tooltip title={`You are marked as ${listToString(project.roles)}`}>
              <span>
                <Emoji symbol="👤" label="star" fontSize={14} />
              </span>
            </Tooltip>
          )}
          <InfoPill label={project.stage ?? '-'} />
        </Stack>
        {poiSizeLabel && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            POI {poiSizeLabel}
          </Typography>
        )}
      </Stack>
    </>
  );
};

interface Props {
  projectListQuery: UseQueryResult<ListResultsQuery<ProjectListEntry>>;
  setHoveredProjectId: Dispatch<SetStateAction<string | null>>;
  searchState: [string, React.Dispatch<React.SetStateAction<string>>];
  starredState: [boolean, storeValue: (value: boolean) => void];
  limitState: [number, React.Dispatch<React.SetStateAction<number>>];
}

function ProjectsList({
  projectListQuery,
  setHoveredProjectId,
  searchState,
  starredState,
  limitState,
}: Readonly<Props>) {
  const [search, setSearch] = searchState;
  const [starred, setStarred] = starredState;
  const [, setLimit] = limitState;
  const clearHoveredProjectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoveredProjectTimeout = () => {
    if (clearHoveredProjectTimeoutRef.current) {
      clearTimeout(clearHoveredProjectTimeoutRef.current);
      clearHoveredProjectTimeoutRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (clearHoveredProjectTimeoutRef.current) {
        clearTimeout(clearHoveredProjectTimeoutRef.current);
        clearHoveredProjectTimeoutRef.current = null;
      }
    },
    [],
  );

  const handleMouseEnter = (project: ProjectListEntry) => {
    clearHoveredProjectTimeout();
    setHoveredProjectId(project.id);
  };

  const handleMouseLeave = () => {
    clearHoveredProjectTimeout();
    clearHoveredProjectTimeoutRef.current = setTimeout(() => {
      setHoveredProjectId(null);
      clearHoveredProjectTimeoutRef.current = null;
    }, clearHoveredProjectDelayMs);
  };

  return (
    <Widget
      title="Projects"
      redirectPath="/projects"
      actions={
        <IconButton
          loading={projectListQuery.isRefetching}
          size="small"
          onClick={() => projectListQuery.refetch()}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
      titleContent={
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Tooltip title={starred ? 'Show all projects' : 'Show my projects'}>
            <ToggleButton
              value="starred"
              selected={starred}
              onChange={() => setStarred(!starred)}
              sx={{ height: 20, width: 20, ml: 1 }}
            >
              <Emoji symbol="👤" label="role" fontSize={18} />
            </ToggleButton>
          </Tooltip>
          <Grid
            sx={{
              width: 150,
              pt: 0.3,
            }}
          >
            <TextField
              value={search}
              placeholder="Search..."
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: 12 },
                },
              }}
              onChange={(e) => setSearch(e.target.value)}
              variant="standard"
              data-testid="search-projects"
              size="small"
            />
          </Grid>
        </Stack>
      }
    >
      <RecordList
        label="project"
        queryResult={projectListQuery}
        getRow={getRow}
        getLink={(project) => `/projects/${project.id}/`}
        idKey="id"
        hasMore={(projectListQuery.data?.count ?? 0) > (projectListQuery.data?.rows.length ?? 0)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onLoadMore={() =>
          setLimit(
            (currentLimit) => Math.min(currentLimit + queryLimit, Number.POSITIVE_INFINITY), // FIXME
          )
        }
        isLoadingMore={projectListQuery.isFetching && !projectListQuery.isLoading}
      />
    </Widget>
  );
}

export default ProjectsList;
